import React, { createContext, useState, useContext, useEffect } from 'react';
import api from './api';
import { toast } from '../utils/toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Проверяем валидность токена через API
          const response = await api.get('/auth/me');
          if (response.data && response.data.user) {
            setUser(response.data.user);
            setIsAuthenticated(true);
            // Обновляем данные пользователя в localStorage
            localStorage.setItem('user', JSON.stringify(response.data.user));
            window.dispatchEvent(new CustomEvent('crm:auth-changed', {
              detail: { authenticated: true },
            }));
          } else {
            // Токен невалиден, очищаем
            console.warn('Invalid token response, clearing auth');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          }
        } catch (error) {
          // Если ошибка 401, токен невалиден - очищаем
          if (error.response?.status === 401) {
            console.warn('Token invalid (401), clearing auth');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setIsAuthenticated(false);
          } else if (error.response?.status === 0 || !error.response) {
            // Сетевая ошибка или CORS проблема - используем сохраненные данные
            console.warn('Network error during auth check, using saved data:', error.message);
            try {
              setUser(JSON.parse(savedUser));
              setIsAuthenticated(true);
            } catch (e) {
              console.error('Error parsing saved user:', e);
              setIsAuthenticated(false);
            }
          } else {
            // Другие ошибки - используем сохраненные данные
            console.warn('Auth check error, using saved data:', error.response?.status, error.message);
            try {
              setUser(JSON.parse(savedUser));
              setIsAuthenticated(true);
            } catch (e) {
              console.error('Error parsing saved user:', e);
              setIsAuthenticated(false);
            }
          }
        }
      } else {
        setIsAuthenticated(false);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      // Проверяем, что ответ содержит токен и пользователя
      if (!response.data || !response.data.token || !response.data.user) {
        console.error('Invalid login response:', response.data);
        toast.error('Некорректный ответ от сервера');
        return { success: false, error: 'Некорректный ответ от сервера' };
      }

      const { token, user } = response.data;

      // Сохраняем токен и пользователя
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Обновляем состояние
      setUser(user);
      setIsAuthenticated(true);
      window.dispatchEvent(new CustomEvent('crm:auth-changed', {
        detail: { authenticated: true },
      }));

      toast.success(`Добро пожаловать, ${user.name}! ☘️`);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);

      // Проверяем, не подтвержден ли email
      if (error.response?.status === 403 && error.response?.data?.email_not_verified) {
        const errorMessage = error.response.data.message || 'Email не подтвержден. Пожалуйста, проверьте вашу почту.';
        toast.error(errorMessage);
        return {
          success: false,
          error: errorMessage,
          email_not_verified: true
        };
      }

      const errorMessage = error.response?.data?.error || error.message || 'Ошибка при входе';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await api.post('/auth/register', { email, password, name });

      // Проверяем ответ от сервера
      if (response.data && response.data.success) {
        // Старые версии API могли возвращать токен сразу после регистрации.
        if (response.data.token && response.data.user) {
          const { token, user } = response.data;
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          setUser(user);
          setIsAuthenticated(true);
          window.dispatchEvent(new CustomEvent('crm:auth-changed', {
            detail: { authenticated: true },
          }));
          toast.success(response.data.message || `Добро пожаловать, ${user.name}!`);
          return { success: true };
        } else {
          // Если токена нет, значит требуется подтверждение email
          toast.success(response.data.message || 'Регистрация успешна! Пожалуйста, проверьте вашу почту для подтверждения email.');
          return {
            success: true,
            message: response.data.message,
            email_verification_required: true
          };
        }
      } else {
        return { success: false, error: response.data.error || 'Ошибка при регистрации' };
      }
    } catch (error) {
      const data = error.response?.data || {};
      toast.error(data.error || 'Ошибка при регистрации');
      if (data.registration_created && data.email_verification_required) {
        return { success: true, email_verification_required: true, message: data.error };
      }
      return { success: false, error: data.error };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    window.dispatchEvent(new CustomEvent('crm:auth-changed', {
      detail: { authenticated: false },
    }));
    toast.info('Вы вышли из системы');
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
